import * as React from "react";
import { FGCustomPanel, FieldGroup, showError, showSuccess, useApiEffect } from "nsitools-react";
import { useApiService, useCrudApi, useTl } from "../hooks";
import { useLocation } from "react-router";
import { ERequestState, ErrorDetails, NeededConditionDto, RequestApi } from "../api";
import { ETLCodes } from "../locales";
import { SmallFormGenerator } from "./SmallFormGenerator";
import { Button } from "@blueprintjs/core";
import { useDialog, useEventsContext } from "../contexts";
import { FGSRSHCheckboxInput } from "../components";
import styled from "styled-components";

export interface IConditionTabProps {
  requestId: number;
  editMode: boolean;
  minLabelWidth?: number;
  statusETLCode: ETLCodes;
  requestState: ERequestState;
}

const StyledFGSRSHCheckboxInput = styled(FGSRSHCheckboxInput)`
  flex: 0;
`;

export const ConditionTab: React.FunctionComponent<IConditionTabProps> = ({
  requestId,
  editMode,
  minLabelWidth = 300,
  statusETLCode,
  requestState,
}) => {
  const { t, tUnsafe } = useTl();
  const { showDialog } = useDialog();
  const { pathname } = useLocation();
  const requestApi = useApiService(RequestApi);
  const [currentRequestStatus, currentRequestStatusIdLoading] = useApiEffect(
    () => requestApi.getStatusCurrentRequest(requestId),
    [requestId]
  );
  const { data, loading, refresh } = useCrudApi<NeededConditionDto[]>(
    React.useMemo(
      () => ({
        getApiFn: (id = requestId) => requestApi.getConditions(id),
        saveApiFn: () => {},
        deleteApiFn: (d) => {},
        onDeletedRoute: () => "",
      }),
      [pathname, requestApi, requestId]
    ),
    [requestId]
  );

  // const ConditionStatuses = React.useMemo(
  //   () =>
  //     !loading &&
  //     data.map((c) => {
  //       return { label: c.requestStatusLabel, code: c.requestStatusCode };
  //     }),
  //   [data, loading]
  // );

  const ConditionStatuses = React.useMemo(
    () => !loading && Array.from(new Set(data.map((c) => c.requestStatusLabel))),
    [data, loading]
  );

  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  const forceCondition = React.useCallback(
    async (neededConditionId) => {
      try {
        showDialog({
          message: t(ETLCodes.ForceConditionConfirmationMessage),
          title: t(ETLCodes.ForceCoditionConfirmationTitle),
          onConfirmed: async () => {
            const res = await requestApi.forceCondition(neededConditionId, requestId);
            showSuccess(t(ETLCodes.ConditionForcedsuccessfully));
            refresh();
            return res;
          },
        });
      } catch (err) {
        const details: ErrorDetails = JSON.parse(err.response);
        showError(tUnsafe(`BackendError_${details.errorCode}`));
      }
    },
    [refresh, requestApi, requestId, showDialog, t, tUnsafe]
  );

  return (
    <>
      <SmallFormGenerator
        initialValues={{ conditions: data }}
        editMode={editMode}
        minLabelWidth={minLabelWidth}
        onSubmit={() => {}}
        loading={loading}
        hideButtons={true}
      >
        {!loading &&
          !currentRequestStatusIdLoading &&
          ConditionStatuses?.map((RequestStatusLabel, i, k) => {
            return (
              <FieldGroup
                columns={1}
                fieldsetProps={{
                  title: (
                    <div
                      style={
                        currentRequestStatus ===
                        data.find((c) => c.requestStatusLabel === RequestStatusLabel).requestStatusCode
                          ? { fontWeight: "bold" }
                          : null
                      }
                    >
                      {RequestStatusLabel}
                    </div>
                  ),
                }}
                // rightElement={
                //   <Button
                //     text={t(ETLCodes.Ask)}
                //     onClick={() => {}}
                //     icon={<i className="fas fa-envelope"></i>}
                //     minimal={true}
                //     intent={Intent.PRIMARY}
                //   />
                // }
              >
                {data?.map((d, index, key) => {
                  return d.requestStatusLabel === RequestStatusLabel ? (
                    <FieldGroup key={index}>
                      <FGCustomPanel>
                        {(ctx) => (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-start",
                              width: "50%",
                            }}
                          >
                            <StyledFGSRSHCheckboxInput
                              name={`conditions[${index}].value`}
                              label={d.label}
                              readonly={true}
                            />
                            {editMode && !d.forced && d.forcible && !d.value && d.requestStatusCode === requestState ? (
                              <Button
                                style={{ maxHeight: "30px" }}
                                text={t(ETLCodes.Force)}
                                onClick={() => forceCondition(d.neededConditionId)}
                                disabled={
                                  currentRequestStatus !==
                                  data.find((c) => c.requestStatusLabel === RequestStatusLabel).requestStatusCode
                                }
                              />
                            ) : d.forced ? (
                              <div style={{ color: "#B22222" }}>{t(ETLCodes.Forced)}</div>
                            ) : (
                              <></>
                            )}
                          </div>
                        )}
                      </FGCustomPanel>
                    </FieldGroup>
                  ) : (
                    <div />
                  );
                })}
              </FieldGroup>
            );
          })}
      </SmallFormGenerator>
    </>
  );
};
